// pothos «intake» — приём скрап-материала в очередь QA (бриф 2026-08-22).
//
// Байты лежат в R2 `intake/<batch_id>/<file_name>` (их кладёт CLI по S3 API
// или потоковый PUT /api/intakePut из браузера); воркер получает манифест
// (register) и докатывает строки в pothos_assets status='qa' сам (process
// по запросу + крон-страховка). Категория/мерчант на входе НЕ нужны — их
// определяет QA. Заводится всё, кроме точных дублей файла/кода.
import { z } from 'zod';
import { AssetCategorySchema } from '../vocab/catalog';

export const INTAKE_SOURCE_KIND = ['local', 'gcs', 'r2', 'browser'] as const;
export const IntakeSourceKindSchema = z.enum(INTAKE_SOURCE_KIND);

// processing — строка захвачена обработчиком (CLI-цикл и крон-свип бегут
// параллельно; без захвата один файл заводился дважды — 100 дублей 2026-08-23).
export const INTAKE_ITEM_STATE = ['pending', 'processing', 'indexed', 'dup_file', 'dup_code', 'failed'] as const;
export const IntakeItemStateSchema = z.enum(INTAKE_ITEM_STATE);

export const PothosIntakeOpenReqSchema = z.object({
  source_kind: IntakeSourceKindSchema.default('local'),
  source_ref: z.string().trim().max(400).default(''),
  // Plan locale_code (ko_KR / de_DE …) — даёт country+language каталога.
  locale: z.string().trim().min(4).max(12),
  category_hint: z.union([AssetCategorySchema, z.literal('')]).default(''),
  aggregator: z.string().trim().max(80).default(''),
  note: z.string().trim().max(400).default(''),
  // Только для CLI-контура (X-Intake-Token): чей это материал. Для
  // пользовательских вызовов игнорируется — берётся email звонящего.
  owner_email: z.string().trim().max(160).default(''),
});
export type PothosIntakeOpenReq = z.infer<typeof PothosIntakeOpenReqSchema>;
export const PothosIntakeOpenResSchema = z.object({
  ok: z.boolean(),
  batch_id: z.string().default(''),
  staging_prefix: z.string().default(''),
  reason: z.string().default(''),
});
export type PothosIntakeOpenRes = z.infer<typeof PothosIntakeOpenResSchema>;

// Строка манифеста = строка CSV скрапера (file, prio, ctype, cval, codes,
// keyword, size, source_page, img_url, sha256); всё, кроме file, опционально.
export const PothosIntakeItemSchema = z.object({
  file: z.string().trim().min(1).max(300),
  sha256: z.string().trim().regex(/^[0-9a-f]{64}$/).or(z.literal('')).default(''),
  // «Code128:123; QRCode:https://…» — как отдаёт скрапер.
  codes: z.string().trim().max(4096).default(''),
  keyword: z.string().trim().max(200).default(''),
  source_page: z.string().trim().max(1000).default(''),
  img_url: z.string().trim().max(1000).default(''),
  prio: z.string().trim().max(8).default(''),
  // "900x1200" из манифеста либо width/height числом.
  size: z.string().trim().max(20).default(''),
  width: z.number().int().min(0).max(20000).default(0),
  height: z.number().int().min(0).max(20000).default(0),
  // Съёмка из манифеста скрапера. До 2026-08-25 intake ставил 'photo'
  // литералом всем подряд — треть кадров уезжала не в ту ячейку плана.
  // Пусто = решает vision на шаге обогащения.
  modality: z.enum(['photo', 'screenshot', '']).default(''),
});
export type PothosIntakeItem = z.infer<typeof PothosIntakeItemSchema>;

export const PothosIntakeRegisterReqSchema = z.object({
  batch_id: z.string().uuid(),
  items: z.array(PothosIntakeItemSchema).min(1).max(500),
});
export type PothosIntakeRegisterReq = z.infer<typeof PothosIntakeRegisterReqSchema>;
export const PothosIntakeRegisterResSchema = z.object({
  ok: z.boolean(),
  registered: z.number().int().default(0),
  total: z.number().int().default(0),
  reason: z.string().default(''),
});

export const PothosIntakeProcessReqSchema = z.object({
  batch_id: z.string().uuid().optional(),
  limit: z.number().int().min(1).max(60).default(30),
});
export const PothosIntakeProcessResSchema = z.object({
  ok: z.boolean(),
  processed: z.number().int().default(0),
  pending: z.number().int().default(0),
});

export const PothosIntakeBatchSchema = z.object({
  batch_id: z.string(),
  owner_email: z.string(),
  source_kind: z.string(),
  source_ref: z.string(),
  locale_code: z.string(),
  country: z.string(),
  category_hint: z.string(),
  aggregator: z.string(),
  note: z.string(),
  total: z.number().int(),
  indexed: z.number().int(),
  dup_file: z.number().int(),
  dup_code: z.number().int(),
  failed: z.number().int(),
  pending: z.number().int(),
  state: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PothosIntakeBatch = z.infer<typeof PothosIntakeBatchSchema>;

export const PothosIntakeStatusResSchema = z.object({
  ok: z.boolean(),
  batch: PothosIntakeBatchSchema.nullable(),
  // Только проблемные строки (не pending/indexed) — чтобы не гнать 1 500 строк.
  issues: z.array(z.object({
    file_name: z.string(), state: IntakeItemStateSchema, asset_id: z.string(), error: z.string(),
  })).default([]),
});
export type PothosIntakeStatusRes = z.infer<typeof PothosIntakeStatusResSchema>;

export const PothosIntakeListResSchema = z.object({
  ok: z.boolean(),
  batches: z.array(PothosIntakeBatchSchema),
  // Plan locale_code'ы для селекта на экране (ключи PACK_LOCALES).
  locales: z.array(z.string()).default([]),
});
export type PothosIntakeListRes = z.infer<typeof PothosIntakeListResSchema>;

export const PothosIntakeCloseReqSchema = z.object({ batch_id: z.string().uuid() });
